"use client";

import React from "react";
import { CircleQuestionMark as CircleHelp, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2, PhoneCall, Clock, Hammer, Trees, Layers, House as Home, TrendingUp, FileText } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const costVsValueData = [{
  project: "Deck addition (composite)",
  averageCost: "$24,984",
  resaleValue: "$20,264",
  recouped: "81.1%"
}, {
  project: "Backyard patio",
  averageCost: "$50,928",
  resaleValue: "$21,107",
  recouped: "41.4%"
}];
const faqItems = [{
  id: "item-1",
  question: "What are the best material choices for decks, screened porches, and custom patios in Georgia?",
  summary: "Comparing pressure-treated lumber, capped composites, tropical hardwoods, flagstone, and motorized screening systems.",
  icon: Layers,
  answer: "Selecting the right outdoor material depends on sun exposure, tree canopy density, and how much ongoing maintenance you prefer. Pressure-treated southern yellow pine remains a budget-friendly natural wood option, though Georgia's high humidity and intense UV cycles require pressure washing, restaining, and sealing every 2 to 3 years. Premium capped composites (such as TimberTech and Trex) and cellular PVC boards resist rot, splintering, and organic staining from damp oak leaves while offering engineered heat-dissipating surface caps that stay cooler under bare feet during July and August. For homeowners seeking natural architectural luxury, sustainably harvested Brazilian hardwoods like Ipe and Cumaru deliver ironwood density and natural insect resistance with a rich walnut-amber patina. For ground-level patios, we install permeable clay brick, natural Tennessee bluestone, and dry-laid travertine pavers on graded gravel bases to manage rainwater runoff efficiently. For screened porches, we integrate fine no-see-um mesh, motorized retractable phantom screens, or Eze-Breeze vinyl-glazed sliding panels that convert an open porch into a comfortable 3-season retreat in cooler autumn and winter months.",
  bulletPoints: ["Pressure-Treated Pine: Classic warmth and lower initial outlay; requires biennial restaining and UV sealing", "Capped Composite & Cellular PVC: 25- to 30-year manufacturer warranties, zero splintering, and soap-and-water upkeep", "Hardwoods & Masonry: Ultra-dense Ipe, Cumaru, Tennessee fieldstone, and permeable pavers for timeless durability", "Porch Conversions: Motorized roll-down insect screens, infrared heaters, and Eze-Breeze 3-season sliding track panels"]
}, {
  id: "item-2",
  question: "How do you handle structural framing, ledgers, and uneven grading on older Decatur bungalows and brick ranches?",
  summary: "Attachment methods for historic masonry and balloon framing, slope management, and independent structural footings.",
  icon: Home,
  answer: "Older Decatur residences—particularly 1920s Craftsman bungalows in Oakhurst, Winnona Park, and Makiel, as well as 1950s mid-century brick ranches—present unique architectural considerations that standard new-construction approaches cannot solve. Many historic bungalows utilize true 2x lumber with balloon framing, multi-wythe structural brick walls, or aged rim joists that cannot safely support a modern tension-loaded ledger board. In these instances, our structural engineers design freestanding, self-supporting deck frames using independent drop beams and concrete piers positioned away from the house, protecting your historic foundation from lateral strain and moisture intrusion. For mid-century brick ranches with single-wythe decorative brick veneer, code strictly prohibits bolting ledger boards directly through brick facade; we install engineered thru-bolts into the internal floor framing with epoxy anchors or specify helical pier support systems. Furthermore, Decatur's rolling topography often features significant backyard grade drops. We engineer terraced retaining walls, stepped multi-level deck platforms, and deep sonotube footings to anchor structures firmly into bedrock or undisturbed clay without causing soil erosion.",
  bulletPoints: ["Freestanding Structural Frameworks: Eliminates ledger stress on historic Decatur craftsman balloon frames and old mortar", "Brick Veneer Ledger Isolation: Engineered through-bolting directly into interior sub-framing or self-bearing support posts", "Uneven Grade Terracing: Stepped multilevel platforms and integrated retaining walls tailored to steep backyard slopes", "Decatur Soil & Foundation Anchoring: 24- to 36-inch deep footings engineered to bypass soft fill dirt and prevent settling"]
}, {
  id: "item-3",
  question: "What is the difference between City of Decatur and unincorporated DeKalb County permitting for decks and patios?",
  summary: "Tree canopy conservation, Critical Root Zone (CRZ) thresholds, and strict impervious surface coverage limits.",
  icon: Trees,
  answer: "Navigating building regulations in our area requires knowing whether your parcel falls within the incorporated City of Decatur municipal boundary or unincorporated DeKalb County. The City of Decatur maintains one of Georgia's strictest tree protection ordinances and comprehensive environmental zoning codes. Any deck, porch, or patio within the city requires rigorous zoning review, tree canopy calculations, and arborist site inspections. In Decatur, projects must strictly respect the Critical Root Zone (CRZ) of protected yard trees (typically restricting root disturbance to less than 20% within the dripline) and adhere to strict impervious surface lot limits (typically 40% maximum coverage in R-60 and R-50 residential zoning). If your lot is near maximum coverage, we engineer permeable slatted decking or pervious paver drainage beds to comply without sacrificing living space. By contrast, unincorporated DeKalb County permitting is administered through the DeKalb County Department of Planning and Sustainability, focusing primarily on standard county setback boundaries, structural IRC load compliance, and local watershed stream buffers. Decatur Heritage Renovations manages all zoning applications, engineered site plans, arborist reviews, and municipal inspections across both jurisdictions.",
  bulletPoints: ["City of Decatur Rules: Mandatory arborist Critical Root Zone (CRZ) plan, tree preservation permits, and 40% impervious limits", "Pervious Engineering: Slatted deck designs and permeable paver sub-bases to satisfy tight municipal drainage quotas", "Unincorporated DeKalb County: Standard county zoning setbacks, environmental watershed clearances, and IRC structural permits", "Full-Service Permitting: Complete preparation of stamped architectural drawings, setback surveys, and city inspection coordination"]
}, {
  id: "item-4",
  question: "What factors commonly extend or shorten the timeline of an outdoor living renovation?",
  summary: "Seasonal Georgia weather, red clay excavation, architectural roof tie-ins, and custom material fabrication lead times.",
  icon: Clock,
  answer: "A standard unroofed composite deck typically takes 2 to 3 weeks of on-site construction, while an expansive gabled screened porch with outdoor fireplace, integrated kitchen, and electrical infrastructure spans 6 to 8 weeks. However, several real-world variables can influence your schedule. Seasonal weather in Georgia is the primary variable: heavy spring storms and prolonged winter rains saturate local red clay, temporarily delaying footing excavations, concrete curing, and heavy grading until soil stability is verified. Another key timeline determinant is structural roofline complexity: tying a new vaulted porch gable cleanly into an existing hip roof or multi-pitch bungalow roofline requires meticulous framing, flashing, and architectural shingle tie-ins that demand extra craftsmanship. Custom material lead times also dictate scheduling; specialized motorized phantom screens, custom powder-coated cable railing systems, and bespoke architectural timber trusses often carry 3- to 5-week fabrication windows. We mitigate delays by ordering all long-lead specialty components during the municipal permitting phase so materials arrive before on-site framing begins.",
  bulletPoints: ["Georgia Climate Delays: Saturated red clay during heavy rainfall can pause grading and footing concrete pours", "Roofline & Masonry Integration: Complex multi-pitch tie-ins, custom flashing, and brick fireplaces require dedicated artisan time", "Early Procurement: Custom motorized screens, steel cable railings, and timber trusses ordered in advance during permitting", "Transparent Milestone Tracking: Dedicated lead builder on site with daily progress logging and weekly schedule forecasts"]
}, {
  id: "item-5",
  question: "How should outdoor living spaces be maintained against heavy Georgia pollen, mildew, and summer humidity?",
  summary: "Care protocols for wood and composite surfaces, gutter maintenance, and screen cleaning schedules.",
  icon: ShieldCheck,
  answer: "Georgia's distinct climate—marked by dense yellow pine pollen in March and April followed by humid summer rainfall—requires a sensible seasonal care routine to keep your outdoor retreat pristine. For natural wood decks and screened porches, we recommend a gentle garden-hose rinse in late spring immediately after pollen season concludes, preventing pollen from embedding deep into wood pores. Pressure-treated wood decks should receive a fresh coat of penetrating oil-based UV sealer every 2 to 3 years, signaled when rainwater no longer beads on the surface. For composite and cellular PVC decking, maintenance is remarkably effortless: a seasonal scrub with warm water, mild biodegradable dish soap, and a soft-bristle deck brush keeps drainage channels clean and prevents organic moss or mold buildup. For screened rooms, vacuuming screen mesh with a soft brush attachment and ensuring overhead gutters remain clear of pine needles will protect soffit framing and maintain optimal ventilation for decades.",
  bulletPoints: ["Spring Pollen Routine: Gentle rinse in late April clears pollen granules before summer heat bakes residue onto boards", "Composite & PVC Care: Zero annual sanding, staining, or harsh chemicals; simple soap and water keeps surfaces immaculate", "Natural Hardwood Upkeep: Annual application of UV-inhibiting hardwood oil (such as Ipe Oil) preserves rich golden-brown luster", "Gutter & Screen Inspection: Bi-annual clearing of roof valley leaves and pine straw prevents water overflow onto framing"]
}];
export default function OutdoorFaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="outdoor-living-faq" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block with Expanded Editorial Intro */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold tracking-wider uppercase border border-border bg-secondary text-secondary-foreground">
              Planning, Permits & Materials
            </Badge>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight mb-5">
            Frequently asked questions about outdoor living in Decatur
          </h2>

          <div className="w-12 h-0.5 bg-primary mx-auto mb-5 rounded-lg" />

          <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed mb-4">
            Creating a seamless outdoor living environment in Decatur requires balancing architectural harmony, durable materials suited to Georgia&apos;s humid climate, and strict adherence to municipal tree preservation and impervious surface ordinances.
          </p>

          <p className="font-sans text-sm sm:text-base text-muted-foreground/90 leading-relaxed">
            Whether you are expanding a 1920s Oakhurst craftsman bungalow, adding a screened porch with vaulted cedar ceilings to a mid-century brick ranch, or transforming an uneven backyard into an outdoor kitchen and stone terrace, explore our detailed engineering, material, and permitting guidance below.
          </p>
        </motion.div>

        {/* Centered Single-Column Accordion */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="max-w-3xl mx-auto space-y-4 mb-14 sm:mb-18">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-4">
            {faqItems.map((faq, index) => {
            const IconComponent = faq.icon;
            return <motion.div key={faq.id} data-index={index} variants={itemVariants} className="rounded-lg overflow-hidden border border-border bg-card shadow-xs transition-[box-shadow,border-color] duration-200 hover:border-primary/40 hover:shadow-sm">
                  <AccordionItem value={faq.id} className="border-b-0">
                    <AccordionTrigger className="px-5 sm:px-6 py-5 hover:no-underline text-left group cursor-pointer data-[state=open]:bg-secondary/40 transition-colors">
                      <div className="flex items-start sm:items-center gap-3.5 pr-2">
                        <div className="size-8 rounded-md bg-secondary text-foreground flex items-center justify-center shrink-0 mt-0.5 sm:mt-0 border border-border group-hover:border-primary/50 transition-colors">
                          <IconComponent className="size-4 text-primary" />
                        </div>
                        <div>
                          <span className="font-serif text-base sm:text-lg font-semibold text-card-foreground leading-snug block">
                            {faq.question}
                          </span>
                          <span className="text-xs sm:text-sm text-muted-foreground font-sans font-normal mt-0.5 block">
                            {faq.summary}
                          </span>
                        </div>
                      </div>
                    </AccordionTrigger>

                    <AccordionContent className="px-5 sm:px-6 pt-2 pb-6 text-card-foreground">
                      <div className="pt-2 border-t border-border/60">
                        <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed mb-4">
                          {faq.answer}
                        </p>

                        <div className="rounded-md bg-secondary/30 p-3.5 sm:p-4 border border-border/80">
                          <h3 className="text-xs font-semibold tracking-wide uppercase text-foreground mb-2 flex items-center gap-1.5">
                            <Hammer className="size-3.5 text-primary" /> Key project considerations
                          </h3>
                          <ul className="space-y-1.5">
                            {faq.bulletPoints.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                                <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                                <span>{point}</span>
                              </li>)}
                          </ul>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>;
          })}
          </Accordion>
        </motion.div>

        {/* Cost vs Value Table Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-3xl mx-auto mb-14 sm:mb-18">
          <div className="rounded-lg border border-border bg-card/85 backdrop-blur-md p-6 sm:p-8 shadow-xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-4 border-b border-border/80">
              <div className="flex items-center gap-2.5">
                <div className="size-8 rounded-md bg-secondary text-foreground flex items-center justify-center shrink-0 border border-border">
                  <TrendingUp className="size-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-serif text-lg sm:text-xl font-semibold text-foreground">
                    Atlanta remodeling cost vs value (2025)
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground font-sans">
                    National survey averages for the Atlanta market, not quotes
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="w-fit text-xs font-medium border-border text-muted-foreground bg-secondary/50">
                Metro ROI Data
              </Badge>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse font-sans text-sm">
                <thead>
                  <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground font-semibold bg-secondary/40">
                    <th scope="col" className="py-3 px-4 rounded-tl-md">Project</th>
                    <th scope="col" className="py-3 px-4">Average Cost</th>
                    <th scope="col" className="py-3 px-4">Resale Value Added</th>
                    <th scope="col" className="py-3 px-4 text-right rounded-tr-md">% Cost Recouped</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {costVsValueData.map((row, idx) => <tr key={row.project} data-index={idx} className="hover:bg-secondary/20 transition-colors">
                      <td className="py-3.5 px-4 font-medium text-foreground">{row.project}</td>
                      <td className="py-3.5 px-4 text-muted-foreground">{row.averageCost}</td>
                      <td className="py-3.5 px-4 text-muted-foreground">{row.resaleValue}</td>
                      <td className="py-3.5 px-4 text-right font-semibold text-primary">{row.recouped}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            <div className="mt-4 pt-3 border-t border-border/60 flex items-center gap-2 text-xs text-muted-foreground font-sans">
              <FileText className="size-3.5 text-primary shrink-0" />
              <span>
                Source: Remodeling Magazine / JLC Cost vs Value Report 2025, Atlanta, GA metro.
              </span>
            </div>
          </div>
        </motion.div>

        {/* Supportive Bottom Help Banner */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.1,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-3xl mx-auto rounded-lg border border-border bg-secondary/60 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-center sm:text-left">
            <div className="size-12 rounded-lg bg-card border border-border flex items-center justify-center shrink-0 shadow-2xs">
              <CircleHelp className="size-6 text-primary" />
            </div>
            <div>
              <h3 className="font-serif text-lg font-semibold text-foreground">
                Have a specific question about your yard?
              </h3>
              <p className="font-sans text-sm text-muted-foreground mt-0.5">
                We review tree buffers, slopes, and architectural ideas during our in-home consult.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild size="default" className="w-full sm:w-auto">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                <span>Book a consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="default" className="w-full sm:w-auto">
              <a href="tel:+14708479076" className="inline-flex items-center justify-center gap-2">
                <PhoneCall className="size-4 text-primary" />
                <span>{"(470) 847-9076"}</span>
              </a>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}